import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, Search, Plus, Eye, Pencil, Trash2, Clock, CheckCircle2,
  XCircle, FileText, ArrowRight, TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { chequeService } from '../api/chequeService';

const STATUS_COLORS = {
  'En attente': 'bg-amber-50 text-amber-600 border-amber-200',
  'Payé': 'bg-emerald-50 text-emerald-600 border-emerald-200',
  'Annulé': 'bg-red-50 text-red-600 border-red-200',
  'Brouillon': 'bg-gray-50 text-gray-600 border-gray-200',
};

const STATUS_ICONS = {
  'En attente': Clock,
  'Payé': CheckCircle2,
  'Annulé': XCircle,
  'Brouillon': FileText,
};

function Cheques() {
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [cheques, setCheques] = useState([]);
  const [stats, setStats] = useState({ total_count: 0, total_amount: 0, en_attente_count: 0, payes_count: 0 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tous les statuts');
  const [bankFilter, setBankFilter] = useState('Toutes les banques');

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null, cheque_number: '', beneficiary: '', bank: '',
    amount: '', amount_words: '', issue_date: '', due_date: '',
    location: '', notes: '', status: 'En attente'
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const formatCurrency = (val) => new Intl.NumberFormat('fr-FR', { style: 'decimal', minimumFractionDigits: 2 }).format(val || 0) + ' DA';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const chequesData = await chequeService.getCheques(currentUser, {
        search,
        status: statusFilter !== 'Tous les statuts' ? statusFilter : undefined,
        bank: bankFilter !== 'Toutes les banques' ? bankFilter : undefined
      });
      setCheques(chequesData);

      const statsData = await chequeService.getStats(currentUser);
      setStats(statsData);
    } catch (error) {
      toast.error('Erreur lors du chargement des chèques');
    } finally {
      setLoading(false);
    }
  }, [currentUser, search, statusFilter, bankFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = (cheque = null) => {
    if (cheque) {
      setFormData({
        id: cheque.id,
        cheque_number: cheque.cheque_number,
        beneficiary: cheque.beneficiary,
        bank: cheque.bank,
        amount: cheque.amount,
        amount_words: cheque.amount_words || '',
        issue_date: cheque.issue_date,
        due_date: cheque.due_date,
        location: cheque.location || '',
        notes: cheque.notes || '',
        status: cheque.status
      });
      setIsEditing(true);
    } else {
      setFormData({
        id: null, cheque_number: '', beneficiary: '', bank: '',
        amount: '', amount_words: '', issue_date: '', due_date: '',
        location: '', notes: '', status: 'En attente'
      });
      setIsEditing(false);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e, draft = false) => {
    e?.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        status: draft ? 'Brouillon' : formData.status
      };

      if (isEditing) {
        await chequeService.updateCheque(currentUser, formData.id, payload);
        toast.success('Chèque mis à jour');
      } else {
        await chequeService.createCheque(currentUser, payload);
        toast.success('Chèque créé');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await chequeService.deleteCheque(currentUser, itemToDelete.id);
      toast.success('Chèque supprimé');
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6 pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-blue-600" />
            Chèques
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gestion et suivi des chèques de la pharmacie</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" /> Nouveau chèque
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500">Total des chèques</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{stats.total_count}</h3>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-amber-600">En attente</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{stats.en_attente_count}</h3>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-emerald-600">Payés</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{stats.payes_count}</h3>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500">Montant total</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(stats.total_amount)}</h3>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-gray-100 shadow-sm">
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher un bénéficiaire, un numéro de chèque..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
          <Select value={bankFilter} onValueChange={setBankFilter}>
            <SelectTrigger className="w-full sm:w-[200px] bg-white">
              <SelectValue placeholder="Banque" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Toutes les banques">Toutes les banques</SelectItem>
              <SelectItem value="BNA">BNA</SelectItem>
              <SelectItem value="BEA">BEA</SelectItem>
              <SelectItem value="CPA">CPA</SelectItem>
              <SelectItem value="BADR">BADR</SelectItem>
              <SelectItem value="SGA">Société Générale</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[200px] bg-white">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tous les statuts">Tous les statuts</SelectItem>
              <SelectItem value="En attente">En attente</SelectItem>
              <SelectItem value="Payé">Payé</SelectItem>
              <SelectItem value="Annulé">Annulé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N° Chèque</TableHead>
              <TableHead>Bénéficiaire</TableHead>
              <TableHead>Banque</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Date d'émission</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cheques.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                  Aucun chèque trouvé
                </TableCell>
              </TableRow>
            ) : (
              cheques.map((cheque) => {
                const StatusIcon = STATUS_ICONS[cheque.status] || Clock;
                return (
                  <TableRow key={cheque.id}>
                    <TableCell className="font-medium text-gray-900">{cheque.cheque_number}</TableCell>
                    <TableCell>{cheque.beneficiary}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-700">
                          {cheque.bank.substring(0, 3)}
                        </div>
                        {cheque.bank}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(cheque.amount)}</TableCell>
                    <TableCell>{format(new Date(cheque.issue_date), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{format(new Date(cheque.due_date), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${STATUS_COLORS[cheque.status]}`}>
                        <StatusIcon className="w-3 h-3" />
                        {cheque.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenModal(cheque)}>
                          <Eye className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenModal(cheque)}>
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setItemToDelete(cheque); setShowDeleteModal(true); }}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* New/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white h-[90vh] flex flex-col">
          <div className="p-6 bg-white border-b border-gray-100 flex justify-between items-center shrink-0">
            <div>
              <DialogTitle className="text-xl font-bold">
                {isEditing ? 'Modifier le chèque' : 'Nouveau chèque'}
              </DialogTitle>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-8">
            <div className="flex-1 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Banque *</Label>
                  <Select value={formData.bank} onValueChange={(val) => setFormData({ ...formData, bank: val })}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Sélectionner une banque" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BNA">BNA</SelectItem>
                      <SelectItem value="BEA">BEA</SelectItem>
                      <SelectItem value="CPA">CPA</SelectItem>
                      <SelectItem value="BADR">BADR</SelectItem>
                      <SelectItem value="SGA">Société Générale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>N° du chèque *</Label>
                  <Input
                    placeholder="Ex: 00012345"
                    value={formData.cheque_number}
                    onChange={(e) => setFormData({ ...formData, cheque_number: e.target.value })}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bénéficiaire *</Label>
                <Input
                  placeholder="Rechercher un bénéficiaire..."
                  value={formData.beneficiary}
                  onChange={(e) => setFormData({ ...formData, beneficiary: e.target.value })}
                  className="bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant [DA] *</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 125 000,00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Montant en lettres</Label>
                  <Input
                    placeholder="Sera généré automatiquement"
                    value={formData.amount_words}
                    onChange={(e) => setFormData({ ...formData, amount_words: e.target.value })}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date d'émission *</Label>
                  <Input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'échéance *</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lieu</Label>
                  <Input
                    placeholder="Ex: Alger"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    placeholder="Informations complémentaires..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-white"
                  />
                </div>
              </div>
              
              {isEditing && (
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="En attente">En attente</SelectItem>
                      <SelectItem value="Payé">Payé</SelectItem>
                      <SelectItem value="Annulé">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="lg:w-[500px] flex flex-col gap-4 mt-6 lg:mt-0 lg:pl-6 lg:border-l lg:border-gray-100">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-800 flex items-center">
                  <Eye className="w-4 h-4 mr-2 text-blue-500" /> Aperçu du chèque
                </h3>
              </div>

              {/* Realistic Cheque Design */}
              <div className="relative w-full aspect-[2.1/1] rounded shadow-md border border-gray-300 bg-[#fdfdfd] overflow-hidden p-4 flex flex-col justify-between font-sans">
                {/* Cheque Background Pattern (Subtle grid/lines) */}
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #000 25%, transparent 25%, transparent 75%, #000 75%, #000), repeating-linear-gradient(45deg, #000 25%, #fff 25%, #fff 75%, #000 75%, #000)', backgroundPosition: '0 0, 10px 10px', backgroundSize: '20px 20px' }}></div>
                
                {/* Barré Lines */}
                <div className="absolute top-0 left-12 w-[1px] h-[150%] bg-red-500/40 transform -rotate-[35deg] origin-top-left z-0"></div>
                <div className="absolute top-0 left-16 w-[1px] h-[150%] bg-red-500/40 transform -rotate-[35deg] origin-top-left z-0"></div>

                {/* Top Row: Bank Info, Title, Amount */}
                <div className="relative z-10 flex justify-between items-start w-full">
                  <div className="w-1/3">
                    <div className="font-extrabold text-gray-800 text-lg uppercase tracking-tight leading-none">{formData.bank || 'BANQUE'}</div>
                    <div className="text-[8px] text-gray-500 uppercase mt-0.5 tracking-widest font-semibold">L'Algérie</div>
                  </div>
                  <div className="w-1/3 text-center pt-1">
                    <div className="text-[8px] font-bold text-gray-500 uppercase tracking-widest">Payez contre ce chèque</div>
                    <div className="text-[7px] text-gray-400 uppercase tracking-widest">Non endossable</div>
                  </div>
                  <div className="w-1/3 flex justify-end">
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-700 font-bold">B.P.</span>
                      <div className="border border-gray-800 px-2 py-0.5 bg-white/80 min-w-[120px] text-right font-bold text-gray-900 tracking-wider text-sm shadow-inner">
                        {formData.amount ? formatCurrency(parseFloat(formData.amount)).replace(' DA', '') : ''}
                      </div>
                      <span className="text-[10px] text-gray-700 font-bold">DA</span>
                    </div>
                  </div>
                </div>

                {/* Middle Row: Amount in words, Beneficiary */}
                <div className="relative z-10 space-y-3 w-full pl-6 pr-2 mt-4">
                  <div className="flex items-end text-xs">
                    <div className="whitespace-nowrap text-gray-800 italic pr-2 font-medium">la somme de</div>
                    <div className="flex-1 border-b border-gray-400 pb-0.5 font-medium text-gray-800 px-2 leading-tight">
                      {formData.amount_words || ''}
                    </div>
                  </div>
                  <div className="flex items-end text-xs">
                    <div className="whitespace-nowrap text-gray-800 italic pr-2 font-medium">à l'ordre de</div>
                    <div className="flex-1 border-b border-gray-400 pb-0.5 font-bold text-gray-900 px-2 text-sm leading-tight uppercase">
                      {formData.beneficiary || ''}
                    </div>
                  </div>
                </div>
                
                {/* Bottom Row: Signature, Location, Date */}
                <div className="relative z-10 flex justify-between items-end mt-4 mb-2">
                  <div className="w-1/2 pl-6">
                    <div className="text-gray-500 text-[9px] uppercase font-bold tracking-widest mb-1">Signature</div>
                  </div>
                  <div className="w-1/2 flex flex-col items-end gap-1.5 pr-2">
                    <div className="flex items-end text-xs w-full max-w-[180px]">
                      <div className="whitespace-nowrap text-gray-800 italic pr-2 text-[10px] font-medium">A</div>
                      <div className="flex-1 border-b border-gray-400 pb-0 font-medium text-gray-800 px-2 text-center text-xs">
                        {formData.location || ''}
                      </div>
                    </div>
                    <div className="flex items-end text-xs w-full max-w-[180px]">
                      <div className="whitespace-nowrap text-gray-800 italic pr-2 text-[10px] font-medium">Le</div>
                      <div className="flex-1 border-b border-gray-400 pb-0 font-medium text-gray-800 px-2 text-center text-xs">
                        {formData.issue_date ? format(new Date(formData.issue_date), 'dd/MM/yyyy') : ''}
                      </div>
                    </div>
                  </div>
                </div>

                {/* MICR Line */}
                <div className="absolute bottom-2 left-0 w-full px-8 flex justify-center items-center opacity-80">
                  <div className="font-mono text-lg tracking-[-0.05em] text-gray-800" style={{ fontFamily: 'monospace' }}>
                    {"<"}{formData.cheque_number || '0000000'}{"<  : 01023  : 1234567890 :"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white border-t border-gray-100 flex justify-end gap-3 shrink-0">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            {!isEditing && (
              <Button variant="outline" onClick={(e) => handleSubmit(e, true)}>
                Enregistrer comme brouillon
              </Button>
            )}
            <Button onClick={(e) => handleSubmit(e)} className="bg-blue-600 hover:bg-blue-700">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce chèque ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default Cheques;
